<template>
  <v-container v-if="$can('browse', 'DocumentTemplate')">
    <v-row>
      <v-col>
        <h1 class="headline mb-12">
          Document Templates
        </h1>
      </v-col>
      <v-col class="shrink">
        <v-btn
          v-if="$can('create', 'DocumentTemplate')"
          color="primary"
          class="ma-0"
          @click="openDocumentTemplateEditDialog()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>

    <v-simple-table class="elevation-5">
      <thead class="v-data-table-header">
        <tr>
          <th style="width: 1px" />
          <th>Title</th>
          <th>Editable</th>
          <th>Countries</th>
          <th>
            Finance Models
          </th>
        </tr>
      </thead>
      <draggable
        v-model="documentTemplates.data"
        tag="tbody"
        handle=".sortHandle"
        element="tr"
        @change="onDocumentTemplateMoved"
      >
        <tr
          v-for="item in documentTemplates.data"
          :key="item.id"
          :class="{'clickable': $can('edit', 'DocumentTemplate')}"
          @click="openDocumentTemplateEditDialog(item)"
        >
          <td>
            <v-icon
              small
              class="sortHandle"
              style="cursor: grab"
            >
              drag_handle
            </v-icon>
          </td>
          <td style="min-width: 500px">
            <v-row>
              <v-col>
                <div>{{ item.title }}</div>
                <div class="grey--text">
                  {{ item.description }}
                </div>
              </v-col>
              <v-col v-if="item.file">
                <a
                  href="#"
                  @click.stop="openFile(item.file.id)"
                >
                  {{ item.file.label }}
                </a>
              </v-col>
            </v-row>
          </td>
          <td>
            <v-chip
              small
              :color="!item.editable ? 'grey' : 'blue'"
              dark
              class="font-weight-bold ma-0"
            >
              {{ !item.editable ? 'Locked' : 'Editable' }}
            </v-chip>
          </td>
          <td class="text-no-wrap">
            <flag
              v-for="country in item.countries"
              :key="country.id"
              :iso="country.code"
              :squared="false"
              class="mr-1"
            />
          </td>
          <td class="text-wrap">
            <v-chip
              v-for="financeModel in item.finance_models"
              :key="financeModel.id"
              small
            >
              <flag
                :iso="financeModel.country.code"
                :squared="false"
                class="mr-1"
              />
              {{ financeModel.label }}
            </v-chip>
          </td>
        </tr>
      </draggable>
    </v-simple-table>

    <document-template-edit-dialog
      :visible.sync="documentTemplateEditDialogVisible"
      :document-template="documentTemplateDirty"
      @documentTemplate:updated="documentTemplateUpdated"
      @documentTemplate:deleted="documentTemplateDeleted"
    />
  </v-container>
</template>

<script>
import draggable from 'vuedraggable'
import DocumentTemplate from '@/models/DocumentTemplate'
import DocumentTemplateEditDialog from '@/components/settings/documentTemplates/DocumentTemplateEditDialog'
import File from '@/models/File'

export default {
  name: 'DocumentTemplatesIndex',
  components: {
    DocumentTemplateEditDialog,
    draggable
  },
  props: {},
  data() {
    return {
      documentTemplates: {
        data: [],
        loading: false,
        meta: {}
      },
      headers: [
        { text: 'Title', value: 'title', sortable: true },
        { text: 'Editable', value: 'editable', sortable: true },
        { text: 'Countries', sortable: false },
        { text: 'Finance Models', sortable: false }
      ],
      documentTemplateDirty: undefined,
      documentTemplateEditDialogVisible: false
    }
  },
  created() {
    this.fetchDocumentTemplates()
  },
  methods: {
    async fetchDocumentTemplates() {
      this.documentTemplates.loading = true

      try {
        this.documentTemplates.data = await DocumentTemplate.api.fetch()
      } catch (e) {
        this.$store.dispatch('alert', {
          'text': 'Something went wrong',
          'color': 'error'
        })
        throw e
      } finally {
        this.documentTemplates.loading = false
      }
    },
    openDocumentTemplateEditDialog(template = undefined) {
      if (!this.$can('edit', 'DocumentTemplate')) return

      this.documentTemplateDirty = template
      this.documentTemplateEditDialogVisible = true
    },
    documentTemplateUpdated(documentTemplate) {
      const index = this.documentTemplates.data.findIndex(dt => dt.id === documentTemplate.id)

      this.documentTemplates.data.splice(index, 1, documentTemplate)
    },
    documentTemplateDeleted(documentTemplateId) {
      const index = this.documentTemplates.data.findIndex(dt => dt.id === documentTemplateId)

      this.documentTemplates.data.splice(index, 1)
    },
    onDocumentTemplateMoved() {
      const items = this.documentTemplates.data.map((item, index) => {
        return {
          id: item.id,
          order: index + 1
        }
      })

      this.updateOrder(items)
    },
    async updateOrder(movedItemsIds) {
      this.documentTemplates.loading = true

      const postData = {
        id_order: movedItemsIds
      }

      try {
        await DocumentTemplate.api.updateOrder(postData)
      } catch (e) {
        this.$store.dispatch('alert', {
          'text': 'Something went wrong',
          'color': 'error'
        })
        throw e
      } finally {
        this.documentTemplates.loading = false
      }
    },
    openFile(fileId) {
      File.api.openDocument(fileId)
    }
  }
}
</script>

<style scoped>

</style>
